/**
 * A data table. On phones each row becomes a card: the first column is the card title,
 * the others are "Header: value" lines, and row actions become full-width buttons. No sideways scroll.
 *
 * columns: [{ key, header, align?: "start"|"end"|"center", width?, render?(row, i), sortable?, hideOnPhone?, primary? }]
 * rows: any[]   rowKey: string | (row) => key
 * sort: { key, dir: "asc" | "desc" }   onSort(key, dir)
 * actions?(row, i): JSX for the actions cell (use plain buttons/links; they go full width on phones)
 * (cockatrice-ui `src/ui/Table.jsx`, ported to hyperscript; PLAN.md §0)
 */
import './css/table.css';
import { createUniqueId, mergeProps, splitProps } from 'solid-js';
import Icon from './Icon.ts';
import { component, each, el, spread, when } from './solid.ts';
import type { Attributes, Child } from './solid.ts';
import { uiText } from './text.ts';

export interface TableColumn<Row extends object = Readonly<Record<string, unknown>>> {
  /** Field name in the row; also the sort key. */
  readonly key: string;
  /** Header text. Use a string so phones can label each value. */
  readonly header: Child;
  /** Text alignment. Use "end" for numbers. Default "start". */
  readonly align?: 'start' | 'end' | 'center' | undefined;
  /** CSS width of the column. */
  readonly width?: string | undefined;
  /** Custom cell content. Default row[key]. */
  readonly render?: ((row: Row, index: number) => Child) | undefined;
  /** Header becomes a sort button. */
  readonly sortable?: boolean | undefined;
  /** Hide this column on phones. */
  readonly hideOnPhone?: boolean | undefined;
  /** This column is the row header and the phone card title. Default: first column. */
  readonly primary?: boolean | undefined;
}
export interface TableSort {
  /** Column key. */
  readonly key: string;
  /** Direction. */
  readonly dir: 'asc' | 'desc';
}
export interface TableProps<Row extends object = Readonly<Record<string, unknown>>> extends Attributes {
  /** Column definitions. */
  readonly columns: readonly TableColumn<Row>[];
  /** Data rows. */
  readonly rows: readonly Row[];
  /** Stable key: a field name or a function. Default: index. */
  readonly rowKey?: string | ((row: Row) => unknown) | undefined;
  /** Current sort. */
  readonly sort?: TableSort | undefined;
  /** Called when a sortable header is clicked. You sort the rows. */
  readonly onSort?: ((key: string, dir: 'asc' | 'desc') => void) | undefined;
  /** Content of the actions cell. Full-width buttons on phones. */
  readonly actions?: ((row: Row, index: number) => Child) | undefined;
  /** Header of the actions column. Default: hidden "Actions". */
  readonly actionsHeader?: Child;
  /** Shown when rows is empty. */
  readonly empty?: Child;
  /** Names the table. */
  readonly caption?: Child;
  /** Caption for screen readers only. */
  readonly captionHidden?: boolean | undefined;
  /** Sticky header row. */
  readonly sticky?: boolean | undefined;
  /** Scroll inside the table above this height (desktop). */
  readonly maxHeight?: string | undefined;
  /** Row click (ignored on inner controls). Always offer a real button too. */
  readonly onRowClick?: ((row: Row, index: number) => void) | undefined;
  /** Key of the highlighted row. */
  readonly selectedKey?: unknown;
  /**
   * Attributes for a row's `<tr>` (a `data-` key a page finds the row by, to move the focus into it), read once as the
   * row is made; the table's own class, selection and click win over them.
   */
  readonly rowAttrs?: ((row: Row, index: number) => Attributes) | undefined;
  /** Tighter cells. */
  readonly dense?: boolean | undefined;
  /** Accessible name when there is no caption. */
  readonly label?: string | undefined;
  /** Extra classes. */
  readonly class?: string | undefined;
}

/** A row's field by name (`row[key]`). */
const field = (row: object, key: string): unknown => {
  const value: unknown = Object.getOwnPropertyDescriptor(row, key)?.value;
  return value;
};

/** A field's value as an element's content, as JSX shows it: text, numbers and nodes as they are; nothing for anything else. */
const shown = (value: unknown): Child => {
  if (value === null || value === undefined || typeof value === 'boolean') return null;
  if (typeof value === 'string' || typeof value === 'number' || value instanceof Node) return value;
  return typeof value === 'bigint' ? value.toString() : null;
};

export default function Table<Row extends object = Readonly<Record<string, unknown>>>(props: TableProps<Row>): Child {
  const [local, rest] = splitProps(props, [
    'class',
    'columns',
    'rows',
    'rowKey',
    'sort',
    'onSort',
    'actions',
    'actionsHeader',
    'empty',
    'caption',
    'captionHidden',
    'sticky',
    'maxHeight',
    'onRowClick',
    'selectedKey',
    'rowAttrs',
    'dense',
    'label',
  ]);
  const cid = createUniqueId();
  const keyOf = (row: Row, i: number): unknown => {
    const key = local.rowKey;
    if (typeof key === 'function') return key(row);
    return key !== undefined && key !== '' ? field(row, key) : i;
  };
  const primaryKey = (): string | undefined => (local.columns.find((c) => c.primary === true) ?? local.columns[0])?.key;
  const colCount = (): number => local.columns.length + (local.actions ? 1 : 0);
  const captioned = (): boolean => Boolean(local.caption);
  const ariaSort = (c: TableColumn<Row>): string | undefined => {
    if (c.sortable !== true) return undefined;
    if (local.sort?.key !== c.key) return 'none';
    return local.sort.dir === 'desc' ? 'descending' : 'ascending';
  };
  const toggle = (c: TableColumn<Row>): void => {
    const dir = local.sort?.key === c.key && local.sort.dir === 'asc' ? 'desc' : 'asc';
    local.onSort?.(c.key, dir);
  };
  const cell = (c: TableColumn<Row>, row: Row, i: number): Child => (c.render ? c.render(row, i) : shown(field(row, c.key)));
  const label = (c: TableColumn<Row>): string | undefined => (typeof c.header === 'string' ? c.header : undefined);

  return el(
    'div',
    mergeProps({
      class: () => ['ui-table', local.sticky === true && 'ui-table--sticky', local.dense === true && 'ui-table--dense', local.class].filter(Boolean).join(' '),
      style: () => (local.maxHeight !== undefined && local.maxHeight !== '' ? { 'max-height': local.maxHeight } : undefined),
    }, rest),
    el(
      'table',
      {
        class: 'ui-table__table',
        'aria-labelledby': () => (captioned() ? cid : undefined),
        'aria-label': () => (captioned() ? undefined : local.label),
      },
      when(captioned, () => el('caption', { id: cid, class: () => (local.captionHidden === true ? 'sr-only' : 'ui-table__caption') }, () => local.caption)),
      el(
        'thead',
        {},
        el(
          'tr',
          {},
          each(
            () => local.columns,
            (c) =>
              el(
                'th',
                {
                  scope: 'col',
                  class: 'ui-table__th',
                  'data-align': () => c.align ?? 'start',
                  style: () => (c.width !== undefined && c.width !== '' ? { width: c.width } : undefined),
                  'aria-sort': () => ariaSort(c),
                },
                when(
                  () => c.sortable === true,
                  () =>
                    el(
                      'button',
                      { type: 'button', class: 'ui-table__sort', onClick: () => {
                          toggle(c);
                        },
                      },
                      () => c.header,
                      el(
                        'span',
                        { class: 'ui-table__sort-icon', 'data-dir': () => (local.sort?.key === c.key ? local.sort.dir : 'none'), 'aria-hidden': 'true' },
                        component(Icon, {
                          get name() {
                            return local.sort?.key === c.key ? (local.sort.dir === 'desc' ? 'chevronDown' : 'chevronUp') : 'sort';
                          },
                          size: 'sm',
                        }),
                      ),
                    ),
                  () => () => c.header,
                ),
              ),
          ),
          when(
            () => Boolean(local.actions),
            () =>
              el(
                'th',
                { scope: 'col', class: 'ui-table__th ui-table__th--actions', 'data-align': 'end' },
                when(
                  () => Boolean(local.actionsHeader),
                  () => () => local.actionsHeader,
                  () => el('span', { class: 'sr-only' }, () => uiText()('ui.table.actions')),
                ),
              ),
          ),
        ),
      ),
      el(
        'tbody',
        {},
        when(
          () => local.rows.length > 0,
          () =>
            each(
              () => local.rows,
              (row, i) => {
                const k = keyOf(row, i());
                return el(
                  'tr',
                  spread(local.rowAttrs?.(row, i()) ?? {}, {
                    class: () => ['ui-table__row', local.onRowClick && 'ui-table__row--clickable'].filter(Boolean).join(' '),
                    'data-selected': () => (local.selectedKey !== null && local.selectedKey !== undefined && local.selectedKey === k ? 'true' : undefined),
                    onClick: (e: MouseEvent) => {
                      const target = e.target;
                      if (local.onRowClick && !(target instanceof Element && target.closest('button,a,input,select,textarea,label'))) local.onRowClick(row, i());
                    },
                  }),
                  each(
                    () => local.columns,
                    (c) =>
                      c.key === primaryKey()
                        ? el(
                            'th',
                            { scope: 'row', class: 'ui-table__td ui-table__td--primary', 'data-align': () => c.align ?? 'start', 'data-label': () => label(c) },
                            () => cell(c, row, i()),
                          )
                        : el(
                            'td',
                            {
                              class: () => ['ui-table__td', c.hideOnPhone === true && 'ui-table__td--hide-phone'].filter(Boolean).join(' '),
                              'data-align': () => c.align ?? 'start',
                              'data-label': () => label(c),
                            },
                            () => cell(c, row, i()),
                          ),
                  ),
                  when(
                    () => Boolean(local.actions),
                    () => el('td', { class: 'ui-table__td ui-table__td--actions', 'data-align': 'end' }, el('div', { class: 'ui-table__actions' }, () => local.actions?.(row, i()))),
                  ),
                );
              },
            ),
          () =>
            el(
              'tr',
              { class: 'ui-table__empty-row' },
              el('td', { colSpan: colCount, class: 'ui-table__empty' }, () => local.empty ?? uiText()('ui.table.empty')),
            ),
        ),
      ),
    ),
  );
}
